/**
 * feAI – Frontend
 * src/lib/permissionsApi.ts
 *
 * API client per il modulo Ruoli e Permessi.
 */

import { apiClient } from './apiClient';

// ── Tipi ──────────────────────────────────────────────────────────────────────

export type PermissionScope = 'tutti' | 'canale' | 'ruolo' | 'agente';

export interface PageCatalogItem {
  page_id:       string;
  label:         string;
  description:   string | null;
  default_allow: boolean;
  sort_order:    number;
  is_active:     boolean;
}

export interface PermissionRule {
  id:             string;
  page_id:        string;
  page_label?:    string;
  scope:          PermissionScope;
  canale:         string | null;
  id_role:        number | null;
  id_role_label?: string | null;
  cod_agente:     number | null;
  cod_agente_label?: string | null;
  can_view:       boolean;
  can_use:        boolean;
  can_view_all:   boolean;
  can_upload:     boolean;
  created_by:     string | null;
  created_at:     string;
  updated_at:     string;
}

export interface RoleOption {
  id_role: number;
  nome:    string;
  sigla:   string;
  canale:  string;
}

export interface AgentOption {
  cod_agente:  number;
  nome:        string | null;
  cognome:     string | null;
  descrizione: string | null;
  canale:      string | null;
  id_role:     number | null;
}

export interface EffectivePagePermission {
  page_id:  string;
  label:    string;
  can_view: boolean;
  can_use:  boolean;
  can_view_all: boolean;
  can_upload:   boolean;
  source:   'admin' | 'agente' | 'ruolo' | 'canale' | 'tutti' | 'default';
}

// ── Funzioni API ──────────────────────────────────────────────────────────────

export async function fetchPermissionsCatalog(): Promise<PageCatalogItem[]> {
  const { data } = await apiClient.get('/api/v1/core/admin/permissions/pages');
  return data.data;
}

export async function fetchPermissionRules(): Promise<PermissionRule[]> {
  const { data } = await apiClient.get('/api/v1/core/admin/permissions/rules');
  return data.data;
}

export async function fetchRoles(): Promise<RoleOption[]> {
  const { data } = await apiClient.get('/api/v1/core/admin/permissions/roles');
  return data.data;
}

export async function searchAgents(query: string): Promise<AgentOption[]> {
  const { data } = await apiClient.get('/api/v1/core/admin/permissions/agents', {
    params: { search: query, limit: 20 },
  });
  return data.data;
}

export async function createRule(input: {
  page_id: string;
  scope: PermissionScope;
  canale?: string | null;
  id_role?: number | null;
  cod_agente?: number | null;
  can_view?: boolean;
  can_use?: boolean;
  can_view_all?: boolean;
  can_upload?: boolean;
}): Promise<PermissionRule> {
  const { data } = await apiClient.post('/api/v1/core/admin/permissions/rules', input);
  return data.data;
}

export async function updateRule(
  id: string,
  input: Partial<{
    scope: PermissionScope;
    canale: string | null;
    id_role: number | null;
    cod_agente: number | null;
    can_view: boolean;
    can_use: boolean;
    can_view_all: boolean;
    can_upload: boolean;
  }>
): Promise<PermissionRule> {
  const { data } = await apiClient.patch(`/api/v1/core/admin/permissions/rules/${id}`, input);
  return data.data;
}

export async function deleteRule(id: string): Promise<void> {
  await apiClient.delete(`/api/v1/core/admin/permissions/rules/${id}`);
}

export async function updatePageDefault(pageId: string, defaultAllow: boolean): Promise<PageCatalogItem> {
  const { data } = await apiClient.patch(`/api/v1/core/admin/permissions/pages/${pageId}/default`, {
    default_allow: defaultAllow,
  });
  return data.data;
}

// ── Permessi utente corrente ─────────────────────────────────────────────────

export async function fetchMyPermissions(): Promise<EffectivePagePermission[]> {
  const { data } = await apiClient.get('/api/v1/core/admin/permissions/my');
  return data.data.pages;
}